import { Component, computed, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { RemoteConfigService } from '@backbase/remote-config-ang';
import { Store } from '@ngrx/store';
import { PERMISSIONS } from '../../auth/permissions';
import { RetailAppRemoteConfig } from '../../remote-config/remote-config';
import { trigger, transition, style, animate, animateChild, query, stagger } from '@angular/animations';
import { RtcCommunicationService } from '@backbase/real-time-communication-journey-ang';
import { BillersSharedDataService } from '@sbk/feature/bill-payments';
import { selectFeatureByIdentifier } from '@sbk/feature/feature-flags-and-limits';

/**
 * providerId of the Credit Card biller in the omni-billers list. Used to
 * pre-select it in the "Select biller" field when the user lands on the
 * payment page via "Pay Credit Card" (mirrors the pending-bills "Pay" flow).
 */
const CREDIT_CARD_BILLER_PROVIDER_ID = 'CCARD';
const APPLY_CREDIT_CARD_FEATURE_IDENTIFIER = 'APPLY_CREDIT_CARD';

@Component({
  selector: 'bb-navigation-menu',
  templateUrl: './navigation-menu.component.html',
  styleUrls: ['./navigation-menu.component.scss'],
  animations: [
    trigger('dropdownFadeSlide', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(-10px)' }),
        animate('400ms ease-out', style({ opacity: 1, transform: 'translateY(0)' }))
      ]),
      transition(':leave', [
        animate('300ms ease-in', style({ opacity: 0, transform: 'translateY(-10px)' }))
      ])
    ]),
    trigger('menuItemsStagger', [
      transition(':enter', [
        query('@menuItemAnim', [
          stagger(50, animateChild())
        ], { optional: true }),
      ])
    ]),
    trigger('menuItemAnim', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateX(-10px)' }),
        animate('400ms ease-out', style({ opacity: 1, transform: 'translateX(0)' }))
      ])
    ])
  ]
})
export class NavigationMenuComponent {
  readonly permissions = PERMISSIONS;
  readonly showManageContacts?: boolean; // assign in constructor

  dashboardEnabled = false;

  private readonly store = inject(Store);

  // Feature flag for "Order Credit Card": status 1 or 2 hides the menu item
  // (its entry point); anything else, including a missing flag, shows it.
  private readonly applyCreditCardFeature = toSignal(
    this.store.select(selectFeatureByIdentifier(APPLY_CREDIT_CARD_FEATURE_IDENTIFIER)),
    { initialValue: undefined },
  );
  readonly applyCreditCardUnavailable = computed(() => {
    const status = this.applyCreditCardFeature()?.status;
    return status === 1 || status === 2;
  });

  constructor(
    private readonly router: Router,
    private remoteConfigService: RemoteConfigService<RetailAppRemoteConfig>,
    private rtcCommunicationService: RtcCommunicationService,
    private readonly billersSharedDataService: BillersSharedDataService,
  ) {
    // safe: runs after DI
    this.showManageContacts = this.remoteConfigService.getValue('show_manage_contacts') as unknown as boolean;
  }

  toggleChatWindow() {
    this.rtcCommunicationService.toggleChatWindow();
  }

  /**
   * Seed the shared biller data so the payment page's "Select biller" field
   * auto-selects the Credit Card biller — the same mechanism used when paying a
   * pending bill. The `routerLink` handles navigation to /bill-payments/payment.
   */
  payCreditCard(): void {
    this.billersSharedDataService.setBillerData({ providerId: CREDIT_CARD_BILLER_PROVIDER_ID });
  }

  /**
   * Highlight View Card Details only on the card summary route, not on sibling
   * journeys (e.g. travel advisory) that share the `/self-service/cards` prefix.
   * `routerLinkActive` subset matching would keep both items active; `paths: 'exact'`
   * does not match the lazy-loaded '' child reliably for this link.
   */
  isViewCardDetailsMenuActive(): boolean {
    const path =
      this.router.url.split('?')[0].split('#')[0].replace(/\/+$/, '') || '/';
    const segments = path.split('/').filter(Boolean);
    if (segments[0] !== 'self-service' || segments[1] !== 'cards') {
      return false;
    }
    if (segments[2] === 'travel-advisory') {
      return false;
    }
    return true;
  }
}
